import { useEffect, useState, lazy, Suspense } from 'react'
import { useSeo } from './hooks/useSeo'
import './styles/design-system.css'
import './App.css'

const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })))
const ResourcesPage = lazy(() =>
  import('./pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage })),
)
const CPFPage = lazy(() => import('./pages/CPFPage').then((m) => ({ default: m.CPFPage })))
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })))
const ManifestoPage = lazy(() =>
  import('./pages/ManifestoPage').then((m) => ({ default: m.ManifestoPage })),
)
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)

type Route = 'home' | 'resources' | 'cpf' | 'legal' | 'manifesto' | 'not-found'

const VALID_PATHS = ['/', '/ressources', '/cpf', '/mentions-legales', '/manifeste']

function resolveRoute(path: string): Route {
  switch (path) {
    case '/':
      return 'home'
    case '/ressources':
      return 'resources'
    case '/cpf':
      return 'cpf'
    case '/mentions-legales':
      return 'legal'
    case '/manifeste':
      return 'manifesto'
    default:
      return 'not-found'
  }
}

function App() {
  const [currentRoute, setCurrentRoute] = useState<Route>(() =>
    resolveRoute(window.location.pathname),
  )
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  // SEO dynamique : canonical, og:url, twitter:url
  useSeo(currentPath)

  useEffect(() => {
    // Handle browser back/forward buttons
    const handlePopState = () => {
      const newPath = window.location.pathname
      setCurrentRoute(resolveRoute(newPath))
      setCurrentPath(newPath)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Intercept link clicks for client-side routing
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const anchor = target.closest('a')

      if (anchor && anchor.href) {
        const url = new URL(anchor.href)

        // Only handle internal links
        if (url.origin === window.location.origin) {
          const path = url.pathname

          // Only intercept known routes
          if (VALID_PATHS.includes(path)) {
            e.preventDefault()
            setCurrentRoute(resolveRoute(path))
            setCurrentPath(path)
            window.history.pushState({}, '', path)
            window.scrollTo(0, 0)
          }
        }
      }
    }

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const renderPage = () => {
    switch (currentRoute) {
      case 'resources':
        return <ResourcesPage />
      case 'cpf':
        return <CPFPage />
      case 'legal':
        return <LegalPage />
      case 'manifesto':
        return <ManifestoPage />
      case 'not-found':
        return <NotFoundPage />
      default:
        return <HomePage />
    }
  }

  return (
    <div className="app">
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>{renderPage()}</Suspense>
    </div>
  )
}

export default App
